import React from 'react';
import { Target } from 'lucide-react';
import { C } from '../../theme.js';
import { Alert, Eyebrow, FieldLabel } from '../../components/Primitives.jsx';
import { PValueInput } from './PValueInput.jsx';
import { AuditTable } from './AuditTable.jsx';
import { parseNumeric, isGroupingColumn, resolveLiveRows, mergedColumns, columnUniqueness, NEAR_UNIQUE_RATIO_THRESHOLD } from './columnClassification.js';
import { ColumnsContext, OriginalColumnsContext } from '../StepInspector.jsx';
import { hasUpstreamSplit, SOURCE_ROWS } from '../../data.js';

function getFitRows(rows) {
  if (!rows || !rows.length) return [];
  for (const key of Object.keys(rows[0] || {})) {
    const vals = new Set(rows.map(r => r[key]).filter(v => v !== null && v !== ''));
    if (vals.size > 0 && Array.from(vals).every(v => v === 'train' || v === 'test')) {
      const filtered = rows.filter(r => r[key] === 'train');
      if (filtered.length > 0) return filtered;
      break;
    }
  }
  return rows;
}

function parseVal(v) {
  const n = parseNumeric(v);
  return n === null ? NaN : n;
}

function gammaLn(z) {
  const p = [
    676.5203681218851, -1259.139216722289, 771.3234287776531,
    -176.6150291621406, 12.507343278686905, -0.13857109526572012,
    9.984369578019572e-6, 1.5056327351493116e-7
  ];
  if (z < 0.5) return Math.log(Math.PI / Math.sin(Math.PI * z)) - gammaLn(1 - z);
  z -= 1;
  let x = 0.9999999999998099;
  for (let i = 0; i < p.length; i++) x += p[i] / (z + i + 1);
  const t = z + p.length - 0.5;
  return 0.5 * Math.log(2 * Math.PI) + (z + 0.5) * Math.log(t) - t + Math.log(x);
}

function betacf(a, b, x) {
  const qab = a + b;
  const qap = a + 1.0;
  const qam = a - 1.0;
  let c = 1.0;
  let d = 1.0 - qab * x / qap;
  if (Math.abs(d) < 1e-30) d = 1e-30;
  d = 1.0 / d;
  let h = d;
  for (let m = 1; m <= 100; m++) {
    const m2 = 2 * m;
    let aa = m * (b - m) * x / ((qam + m2) * (a + m2));
    d = 1.0 + aa * d;
    if (Math.abs(d) < 1e-30) d = 1e-30;
    c = 1.0 + aa / c;
    if (Math.abs(c) < 1e-30) c = 1e-30;
    d = 1.0 / d;
    h *= d * c;
    aa = -(a + m) * (qab + m) * x / ((a + m2) * (qap + m2));
    d = 1.0 + aa * d;
    if (Math.abs(d) < 1e-30) d = 1e-30;
    c = 1.0 + aa / c;
    if (Math.abs(c) < 1e-30) c = 1e-30;
    d = 1.0 / d;
    const del = d * c;
    h *= del;
    if (Math.abs(del - 1.0) < 1e-12) break;
  }
  return h;
}

function betai(a, b, x) {
  if (x <= 0) return 0.0;
  if (x >= 1.0) return 1.0;
  const bt = Math.exp(gammaLn(a + b) - gammaLn(a) - gammaLn(b) + a * Math.log(x) + b * Math.log(1.0 - x));
  if (x < (a + 1.0) / (a + b + 2.0)) {
    return bt * betacf(a, b, x) / a;
  } else {
    return 1.0 - bt * betacf(b, a, 1.0 - x) / b;
  }
}

function fPValue(fStat, df1, df2) {
  if (df1 <= 0 || df2 <= 0 || fStat <= 0) return 1.0;
  const x = df2 / (df2 + df1 * fStat);
  return betai(df2 / 2.0, df1 / 2.0, x);
}

function computeAnovaScores(rows, targetCol, maxPValue, manualOverrides = {}, columnsList = []) {
  if (!rows || !rows.length || !targetCol) return [];
  const fitRows = getFitRows(rows);
  const typeOf = (name) => (columnsList.find(c => c.name === name)?.type) || 'string';

  // Candidate features: strictly Numerical, excluding targetCol and __rowIndex
  const candidateCols = columnsList.filter(c => {
    if (c.name === targetCol || c.name === '__rowIndex') return false;
    return c.type === 'number';
  }).map(c => c.name);

  return candidateCols.map(col => {
    // Group numerical feature values by target group
    const groups = {};
    let totalN = 0;
    let totalSum = 0;
    let anyParseable = false;

    for (const r of fitRows) {
      const val = parseVal(r[col]);
      const groupVal = r[targetCol];
      if (isNaN(val) || groupVal === null || groupVal === undefined || groupVal === '') continue;
      anyParseable = true;

      const gKey = String(groupVal);
      groups[gKey] = groups[gKey] || [];
      groups[gKey].push(val);

      totalN += 1;
      totalSum += val;
    }

    const groupKeys = Object.keys(groups);
    const numGroups = groupKeys.length;
    const dataType = typeOf(col);

    if (numGroups < 2 || totalN <= numGroups) {
      return {
        column: col,
        dataType,
        fScore: null,
        pValue: 1.0,
        metric: anyParseable ? 'N/A (insufficient groups)' : 'N/A (unparseable)',
        autoStatus: 'drop',
        override: manualOverrides[col] || 'auto'
      };
    }

    const overallMean = totalSum / totalN;
    let ssBetween = 0;
    let ssWithin = 0;

    for (const g of groupKeys) {
      const gVals = groups[g];
      const gMean = gVals.reduce((a, b) => a + b, 0) / gVals.length;
      ssBetween += gVals.length * Math.pow(gMean - overallMean, 2);
      for (const v of gVals) {
        ssWithin += Math.pow(v - gMean, 2);
      }
    }

    const df1 = numGroups - 1;
    const df2 = totalN - numGroups;

    let fScore = 0.0;
    let pValue = 1.0;

    if (ssWithin === 0) {
      if (ssBetween > 0) {
        fScore = Infinity;
        pValue = 0.0;
      } else {
        fScore = 0.0;
        pValue = 1.0;
      }
    } else {
      const msBetween = ssBetween / df1;
      const msWithin = ssWithin / df2;
      fScore = msBetween / msWithin;
      pValue = fPValue(fScore, df1, df2);
    }

    const autoStatus = pValue <= maxPValue ? 'keep' : 'drop';
    const displayPValue = pValue < 0.0001 ? '< 0.0001' : pValue.toFixed(4);

    return {
      column: col,
      dataType,
      fScore: isFinite(fScore) ? fScore : 999999.0,
      pValue,
      metric: `F=${isFinite(fScore) ? fScore.toFixed(4) : '∞'}, p=${displayPValue}`,
      autoStatus,
      override: manualOverrides[col] || 'auto'
    };
  });
}

export function AnovaInspector({ step, onChange, pipeline, uploadedRows }) {
  const c = step.config || {};
  const targetCol = c.target || '';
  const maxPValue = c.max_p_value ?? 0.05;
  const manualOverrides = c.manual_overrides || {};

  const u = (patch) => onChange({ ...step, config: { ...c, ...patch } });
  const splitReady = pipeline ? hasUpstreamSplit(pipeline.steps, step.id) : true;
  const rows = resolveLiveRows(uploadedRows, pipeline, SOURCE_ROWS);

  const liveColumns = React.useContext(ColumnsContext) || [];
  const origColumns = React.useContext(OriginalColumnsContext) || [];
  const allCols = mergedColumns(origColumns, liveColumns);

  // Target options are restricted to genuinely non-numeric columns — a
  // numeric column (even a low-cardinality one, like Age) is always a
  // continuous FEATURE candidate for ANOVA, never a valid target/grouping
  // variable, regardless of how few distinct values it has. This is
  // deliberately stricter than isCategoricalColumn (used elsewhere for
  // Chi-Square, where a low-cardinality numeric column is a legitimate
  // categorical proxy) — see isGroupingColumn in columnClassification.js.
  const targetCols = React.useMemo(() => {
    const cats = allCols.filter(c => isGroupingColumn(c));
    return cats.length ? cats : allCols;
  }, [allCols]);

  // Early, non-blocking flag at target-selection time — before the user
  // even runs the step — that a near-unique/ID-like target (e.g.
  // Employee_ID) will produce mostly single-row groups. Mirrors the
  // backend's Frequency Encoding near-unique warning's wording/threshold
  // (see columnUniqueness/NEAR_UNIQUE_RATIO_THRESHOLD).
  const targetUniqueness = React.useMemo(
    () => columnUniqueness(getFitRows(rows), targetCol),
    [rows, targetCol]
  );

  const scores = computeAnovaScores(rows, targetCol, maxPValue, manualOverrides, allCols);

  const setOverride = (col, next) => {
    const nextOverrides = { ...manualOverrides };
    if (next === 'auto') {
      delete nextOverrides[col];
    } else {
      nextOverrides[col] = next;
    }
    u({ manual_overrides: nextOverrides });
  };

  return (
    <div>
      <Eyebrow>ANOVA F-Value</Eyebrow>
      <div style={{ color: C.fg4, fontSize: '.78rem', margin: '4px 0 10px', lineHeight: 1.5 }}>
        Evaluate continuous numerical features against a categorical target using One-Way ANOVA F-tests.
      </div>

      {!splitReady && (
        <Alert kind="warn" style={{ marginBottom: 10 }}>
          No Train / Test Split step found upstream — ANOVA statistics are calculated using all rows. Add a Train / Test Split step first to avoid data leakage.
        </Alert>
      )}

      {/* Target Variable Dropdown */}
      <div style={{ marginBottom: 12 }}>
        <FieldLabel hint="Select a categorical target variable representing groups/classes">
          Target Variable (Categorical / Groups)
        </FieldLabel>
        <select
          value={targetCol}
          onChange={(e) => u({ target: e.target.value })}
          style={{
            width: '100%', background: C.input, color: C.fg1,
            border: `1px solid ${C.borderStrong}`, borderRadius: 6,
            padding: '6px 9px', fontSize: '.82rem', outline: 'none',
            fontFamily: 'inherit', marginTop: 4, cursor: 'pointer'
          }}
        >
          <option value="" disabled>Select target column…</option>
          {targetCols.map(col => (
            <option key={col.name} value={col.name}>
              {col.name} ({col.type || 'string'})
            </option>
          ))}
        </select>
      </div>

      {targetUniqueness && targetUniqueness.ratio > NEAR_UNIQUE_RATIO_THRESHOLD && (
        <Alert kind="warn" style={{ marginBottom: 12 }}>
          {`'${targetCol}' is ${Math.round(targetUniqueness.ratio * 100)}% unique in the training data (${targetUniqueness.distinct} distinct values across ${targetUniqueness.total} rows) — as an ANOVA target this will produce mostly single-row groups, which can't produce a meaningful result for any feature. This looks like an ID column; consider a different target.`}
        </Alert>
      )}

      {/* Significance Presets */}
      <div style={{ marginBottom: 12 }}>
        <FieldLabel hint="Maximum p-value threshold for feature retention">Significance Presets</FieldLabel>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {[
            { label: 'Standard (p <= 0.05)', val: 0.05 },
            { label: 'Strict (p <= 0.01)', val: 0.01 },
            { label: 'Relaxed (p <= 0.10)', val: 0.10 },
          ].map(p => {
            const active = maxPValue === p.val;
            return (
              <button
                key={p.label}
                type="button"
                onClick={() => u({ max_p_value: p.val })}
                style={{
                  background: active ? C.accentStrong : C.input,
                  color: active ? '#fff' : C.fg3,
                  border: `1px solid ${active ? C.accentStrong : C.borderStrong}`,
                  borderRadius: 6, padding: '4px 8px', fontSize: '.74rem',
                  fontWeight: 600, cursor: 'pointer', transition: 'all .15s'
                }}
              >
                {p.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Max p-value input */}
      <div style={{ marginBottom: 14 }}>
        <FieldLabel hint="Maximum p-value (features with p-value > max_p_value are dropped)">
          Max p-value threshold: {maxPValue}
        </FieldLabel>
        <PValueInput
          value={maxPValue}
          onChange={(n) => u({ max_p_value: n })}
        />
      </div>

      {/* Live Preview Table */}
      {targetCol ? (
        <AuditTable
          headerLabel={<><Target size={12} style={{ display: 'inline', verticalAlign: -1, marginRight: 5 }} />Evaluating features against Target: {targetCol}</>}
          rows={scores.map(row => ({
            column: row.column,
            dataType: row.dataType,
            metric: row.metric,
            autoStatus: row.autoStatus,
            override: row.override,
            onOverrideChange: (next) => setOverride(row.column, next),
          }))}
          emptyMessage="No numerical candidate features found."
        />
      ) : (
        <div style={{
          marginTop: 10, padding: 12, borderRadius: 8, background: C.surface,
          border: `1px dashed ${C.border}`, color: C.fg4, fontSize: '.76rem', textAlign: 'center'
        }}>
          Please select a categorical target variable above to display live ANOVA F-Values.
        </div>
      )}
    </div>
  );
}

